import { WorkfrontBundleChangeKind } from '@atlas/types'
import { Badge, type BadgeProps, Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { formatUtc } from '@/lib/format-utc'
import type { WorkfrontBundleVersionDTO } from '@/services/workfront.service'
import { humanize } from './StatusBadges'

export interface VersionListProps {
	versions: WorkfrontBundleVersionDTO[]
}

const KIND_VARIANT: Record<string, NonNullable<BadgeProps['variant']>> = {
	[WorkfrontBundleChangeKind.ADDED]: 'success',
	[WorkfrontBundleChangeKind.CHANGED]: 'secondary',
	[WorkfrontBundleChangeKind.REMOVED]: 'danger',
}

/** Changes a version lists before it folds the rest away: a first fetch adds every object of the bundle. */
export const VISIBLE_CHANGES = 10

/** How many objects each kind of change touched, in the order added, changed, removed. */
function kindCounts(changes: WorkfrontBundleVersionDTO['changes']) {
	return Object.values(WorkfrontBundleChangeKind)
		.map((kind) => ({ kind, count: changes.filter((change) => change.kind === kind).length }))
		.filter(({ count }) => count > 0)
}

/** One version: why it was fetched, what it cost, how many objects changed, and each one with the keys that did. */
function VersionItem({ version }: { version: WorkfrontBundleVersionDTO }) {
	const [showAll, setShowAll] = useState(false)
	const { changes } = version
	const shown = showAll ? changes : changes.slice(0, VISIBLE_CHANGES)
	return (
		<li className="border-ds-border border-b py-3 last:border-b-0">
			<div className="flex flex-wrap items-center gap-2 text-ds-sm">
				<span className="font-semibold">v{version.version}</span>
				<Badge size="sm" variant="muted">
					{humanize(version.cause)}
				</Badge>
				<span className="text-ds-text-muted">{formatUtc(version.fetchedAt)}</span>
				<span className="text-ds-text-muted">
					{version.requestCount} requests · {(version.durationMs / 1000).toFixed(1)} s
				</span>
				{kindCounts(changes).map(({ kind, count }) => (
					<span key={kind} className="text-ds-text-muted">
						{count} {kind}
					</span>
				))}
			</div>
			<ul className="mt-2 space-y-1">
				{shown.map((change) => (
					<li key={`${change.kind}:${change.workfrontId}`} className="flex flex-wrap items-center gap-2 text-ds-sm">
						<Badge size="sm" variant={KIND_VARIANT[change.kind] ?? 'muted'}>
							{change.kind}
						</Badge>
						<span>{humanize(change.role)}</span>
						<span className="font-mono text-ds-xs text-ds-text-muted">{change.workfrontId}</span>
						{change.keys.length > 0 ? <span className="text-ds-text-muted">{change.keys.join(', ')}</span> : null}
					</li>
				))}
			</ul>
			{changes.length > VISIBLE_CHANGES ? (
				<Button size="sm" variant="ghost" className="mt-1" onPress={() => setShowAll((current) => !current)}>
					{showAll ? 'Show fewer changes' : `Show all ${changes.length} changes`}
				</Button>
			) : null}
		</li>
	)
}

/** A bundle's versions, newest first, each with its change list. */
export function VersionList({ versions }: VersionListProps) {
	if (versions.length === 0) return <p className="text-ds-sm text-ds-text-muted">No versions yet.</p>
	return (
		<ul aria-label="Versions">
			{versions.map((version) => (
				<VersionItem key={version.version} version={version} />
			))}
		</ul>
	)
}
